import {
  IonBadge,
  IonButton,
  IonButtons,
  IonContent,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonItem,
  IonLabel,
  IonList,
  IonPopover,
  IonSpinner,
  IonTitle,
  IonToolbar,
  useIonToast,
} from '@ionic/react';
import { closeOutline, notifications, settingsOutline } from 'ionicons/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { notifications as notificationsApi, user } from '../services/api.ts';
import BalanceDisplay from './BalanceDisplay.tsx';

interface Notification {
  id: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

interface TitleBarProps {
  title: string;
  children?: React.ReactNode;
}

const TitleBar: React.FC<TitleBarProps> = ({ title, children }) => {
  const history = useHistory();
  const [present] = useIonToast();
  const [unreadCount, setUnreadCount] = useState(0);
  const [popoverState, setPopoverState] = useState<{ show: boolean; event: Event | undefined }>({
    show: false,
    event: undefined
  });
  const [notificationList, setNotificationList] = useState<Notification[]>([]);
  const [isNotificationsLoading, setIsNotificationsLoading] = useState(true);
  const [hasMoreNotifications, setHasMoreNotifications] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const count = await user.getUnreadNotificationCount();
      setUnreadCount(count);
    } catch (error) {
      console.error("Failed to fetch unread notification count", error);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    // Poll every 2 minutes (reduced frequency to reduce server load)
    const intervalId = setInterval(fetchUnreadCount, 120000);
    return () => clearInterval(intervalId);
  }, [fetchUnreadCount]);

  const loadNotifications = async (cursor?: string, append: boolean = false) => {
    try {
      const response = await notificationsApi.list({
        limit: cursor ? 10 : 5, // Load 5 initially, then 10 for subsequent loads
        cursor
      });

      if (append && cursor) {
        setNotificationList(prev => [...prev, ...response.notifications]);
      } else {
        setNotificationList(response.notifications);
      }

      setHasMoreNotifications(response.has_more);
      setNextCursor(response.next_cursor);
    } catch (error) {
      console.error("Failed to fetch notifications", error);
      present({ message: 'Could not load notifications.', duration: 2000, color: 'danger' });
    }
  };

  const handleBellClick = async (e: React.MouseEvent) => {
    e.persist();
    setIsNotificationsLoading(true);
    setPopoverState({ show: true, event: e.nativeEvent });

    try {
      await loadNotifications();
    } finally {
      setIsNotificationsLoading(false);
    }
  };

  const handleLoadMore = async (event: CustomEvent) => {
    if (nextCursor && hasMoreNotifications) {
      await loadNotifications(nextCursor, true);
    }
    (event.target as HTMLIonInfiniteScrollElement)?.complete();
  };

  const handleNotificationClick = async (notificationId: string) => {
    const notification = notificationList.find(n => n.id === notificationId);
    if (!notification || notification.is_read) {
      return;
    }

    setNotificationList(currentList =>
      currentList.map(n =>
        n.id === notificationId ? { ...n, is_read: true } : n
      )
    );

    try {
      await notificationsApi.markAsRead(notificationId);
      setUnreadCount(prevCount => prevCount - 1);
    } catch (error) {
      console.error(`Failed to mark notification ${notificationId} as read`, error);
      setNotificationList(currentList =>
        currentList.map(n =>
          n.id === notificationId ? { ...n, is_read: false } : n
        )
      );
    }
  };

  const handleDismissNotification = async (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering the notification click

    // Optimistically remove from UI
    const notification = notificationList.find(n => n.id === notificationId);
    const wasUnread = notification && !notification.is_read;

    setNotificationList(currentList =>
      currentList.filter(n => n.id !== notificationId)
    );

    if (wasUnread) {
      setUnreadCount(prevCount => Math.max(0, prevCount - 1));
    }

    try {
      await notificationsApi.dismiss(notificationId);
    } catch (error) {
      console.error(`Failed to dismiss notification ${notificationId}`, error);
      present({ message: 'Failed to dismiss notification.', duration: 2000, color: 'danger' });

      // Revert optimistic update on error
      if (notification) {
        setNotificationList(currentList => [...currentList, notification]);
        if (wasUnread) {
          setUnreadCount(prevCount => prevCount + 1);
        }
      }
    }
  };

  return (
    <>
      <IonToolbar>
        <IonTitle>{title}</IonTitle>
        <IonButtons slot="end">
          {children}
          <BalanceDisplay onClick={() => history.push('/app/billing')} />
          <IonButton onClick={() => history.push('/app/settings')}>
            <IonIcon icon={settingsOutline} style={{ fontSize: '26px' }} />
          </IonButton>
          <IonButton onClick={handleBellClick} style={{ position: 'relative' }}>
            <IonIcon icon={notifications} style={{ fontSize: '26px' }} />
            {unreadCount > 0 && (
              <IonBadge
                color="danger"
                style={{
                  position: 'absolute',
                  bottom: '0px',
                  right: '0px',
                  fontSize: '8px',
                  minWidth: '14px',
                  height: '14px',
                  borderRadius: '7px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1,
                  lineHeight: '1',
                  fontWeight: '700',
                  padding: '0'
                }}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </IonBadge>
            )}
          </IonButton>
        </IonButtons>
      </IonToolbar>

      <style>{`
        @media (max-width: 400px) {
          ion-toolbar ion-buttons ion-button {
            --padding-start: 4px;
            --padding-end: 4px;
          }
          ion-toolbar ion-buttons ion-icon {
            font-size: 22px !important;
          }
          ion-toolbar ion-title {
            font-size: 18px;
            padding-inline-start: 8px;
            padding-inline-end: 8px;
          }
        }
      `}</style>

      <IonPopover
        isOpen={popoverState.show}
        event={popoverState.event}
        onDidDismiss={() => setPopoverState({ show: false, event: undefined })}
        side="bottom"
        alignment="end"
        translucent={true}
      >
        <IonContent style={{ "--background": "#fff", maxHeight: '400px' }}>
          <IonList>
            <IonItem lines="full">
              <IonLabel style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Notifications</IonLabel>
            </IonItem>
            {isNotificationsLoading ? (
              <div style={{ padding: '20px', textAlign: 'center' }}><IonSpinner /></div>
            ) : notificationList.length === 0 ? (
              <IonItem>
                <IonLabel>No new notifications.</IonLabel>
              </IonItem>
            ) : (
              <>
                {notificationList.map(notif => (
                  <IonItem
                    key={notif.id}
                    button={true}
                    onClick={() => handleNotificationClick(notif.id)}
                    lines="none"
                    style={{ opacity: notif.is_read ? 0.6 : 1 }}
                  >
                    <IonLabel>
                      <h2 style={{ fontWeight: notif.is_read ? 'normal' : 'bold', fontSize: '0.9rem' }}>{notif.title}</h2>
                      <p style={{ whiteSpace: 'normal', fontSize: '0.8rem' }}>{notif.message}</p>
                      <p style={{ fontSize: '0.7rem', color: '#888', paddingTop: '4px' }}>
                        {new Date(notif.created_at).toLocaleDateString()}
                      </p>
                    </IonLabel>
                    <IonButton
                      fill="clear"
                      color="medium"
                      size="small"
                      slot="end"
                      onClick={(e) => handleDismissNotification(notif.id, e)}
                      style={{ '--padding-start': '8px', '--padding-end': '8px' }}
                    >
                      <IonIcon icon={closeOutline} />
                    </IonButton>
                  </IonItem>
                ))}
                {hasMoreNotifications && (
                  <IonInfiniteScroll
                    threshold="100px"
                    onIonInfinite={handleLoadMore}
                  >
                    <IonInfiniteScrollContent
                      loadingSpinner="bubbles"
                      loadingText="Loading more..."
                    />
                  </IonInfiniteScroll>
                )}
              </>
            )}
          </IonList>
        </IonContent>
      </IonPopover>
    </>
  );
};

export default TitleBar;